import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { showTelegramAlert } from '../utils/telegram';
import { claimPrize, getProductModifications, getProductInfo, consumeProduct, getPrizeAvailableShops } from '../services/api';
import { isTelegramWebApp, getTelegramTheme, applyTelegramTheme } from '../utils/telegram';

const PrizePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [imageError, setImageError] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [theme, setTheme] = useState('light');

  // Новые состояния для выбора товаров
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedModificationId, setSelectedModificationId] = useState(null);
  const [productModifications, setProductModifications] = useState([]);
  const [loadingModifications, setLoadingModifications] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(false);

  // Состояния для MDA магазинов
  const [selectedMdaProduct, setSelectedMdaProduct] = useState(null);
  const [selectedMdaCharacteristics, setSelectedMdaCharacteristics] = useState({});
  const [giftPrize, setGiftPrize] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showNoProductsMessage, setShowNoProductsMessage] = useState(false);

  // Состояния для списка магазинов с товарами
  const [availableShops, setAvailableShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);

  // Защита от двойного клика
  const isProcessingRef = useRef(false);

  // Получаем данные из location.state
  const { prizeData, shopInfo, telegramData } = location.state || {};

  useEffect(() => {
    // Применяем тему Telegram
    const themeData = applyTelegramTheme();
    setTheme(themeData.theme);

    // Сбрасываем флаг обработки при монтировании компонента
    isProcessingRef.current = false;
    setShowNoProductsMessage(false);

    // Проверяем, что все необходимые данные переданы
    if (!prizeData || !shopInfo || !telegramData) {
      showTelegramAlert('❌ Ошибка: недостаточно данных для отображения приза');
      navigate('/');
      return;
    }
  }, [prizeData, shopInfo, telegramData, navigate]);

  const handleBackClick = () => {
    navigate('/');
  };

  // Функция для получения товаров для MDA магазинов
  const getShopProducts = async (shopCode) => {
    const response = await fetch(`/api/catalog/shop-products/${shopCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Shop products response:', JSON.stringify(data, null, 2));

    return data;
  };

  // Функция для получения списка магазинов с товарами для приза
  const getAvailableShops = async (prizeId) => {
    try {
      setLoadingShops(true);
      const data = await getPrizeAvailableShops(prizeId);

      if (data.success && data.data) {
        setAvailableShops(data.data.shops || []);
      } else {
        setAvailableShops([]);
      }
    } catch (error) {
      console.error('Error loading available shops:', error);
      setAvailableShops([]);
    } finally {
      setLoadingShops(false);
    }
  };

  const handleClaimPrize = () => {
    console.log('handleClaimPrize called:', {
      shopType: shopInfo?.shop_type,
      availableProducts: prizeData?.availableProducts?.length,
      showProductSelection,
      isMda: shopInfo?.shop_type === 'mda',
      useStockSystem: prizeData?.useStockSystem,
      pickupGroupCode: prizeData?.prize?.pickupGroupCode,
      stockQuantity: prizeData?.prize?.stockQuantity
    });

    if (showProductSelection) {
      // Если уже показывается выбор товаров - возвращаемся к основной информации
      console.log('Returning to prize info');
      setShowProductSelection(false);
      // Сбрасываем выбранные товары
      setSelectedMdaProduct(null);
      setSelectedMdaCharacteristics({});
      setSelectedProduct(null);
      setSelectedModificationId(null);
      setProductModifications([]);
    } else {
      // ПРИОРИТЕТ 1: Если используется система stock_quantity (для призов партнеров типа gigamag)
      if (prizeData?.useStockSystem === true) {
        console.log('Using stock system - direct claim without product selection');
        // Сразу открываем модальное окно подтверждения
        setShowConfirmationModal(true);
        return;
      }

      // ПРИОРИТЕТ 2: Проверяем тип магазина
      if (shopInfo?.shop_type === 'mda') {
        // Для MDA магазинов - показываем выбор товара для списания через 1С
        console.log('Setting showProductSelection to true for MDA');
        setShowProductSelection(true);
      } else if (prizeData?.availableProducts && prizeData.availableProducts.length > 0) {
        // Для обычных магазинов с доступными товарами - показываем выбор из списка
        console.log('Setting showProductSelection to true for regular shop with products');
        setShowProductSelection(true);
      } else {
        // У магазина нет доступных товаров для этого приза
        console.log('No available products for this prize');
        setShowNoProductsMessage(true);
        // Загружаем список магазинов, где есть товары для этого приза
        getAvailableShops(prizeData.prize.id);
      }
    }
  };

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);

    // Загружаем модификации товара
    if (product.modifications && product.modifications.length > 0) {
      setProductModifications(product.modifications);
    } else {
      // Если модификации не загружены, пытаемся получить их с сервера
      try {
        setLoadingModifications(true);
        const result = await getProductModifications(product.id, shopInfo?.shop_code);
        if (result.success && result.data) {
          setProductModifications(result.data.modifications || []);
        } else {
          setProductModifications([]);
        }
      } catch (error) {
        console.error('Error loading product modifications:', error);
        setProductModifications([]);
      } finally {
        setLoadingModifications(false);
      }
    }
  };

  const handleMdaProductSelect = (product) => {
    setSelectedMdaProduct(product);
    // Характеристики уже загружены в товаре
    setSelectedMdaCharacteristics(product.characteristics || {});
  };

  const handleMdaCharacteristicSelect = (key, value) => {
    setSelectedMdaCharacteristics(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleModificationSelect = (modificationId) => {
    console.log('Выбрана модификация:', modificationId);
    setSelectedModificationId(modificationId);
    setShowConfirmationModal(true);
  };

  const handleConfirmProductSelection = async () => {
    // Защита от двойного клика
    if (isProcessingRef.current || isClaiming) {
      return;
    }

    isProcessingRef.current = true;
    setIsClaiming(true);

    try {
      // Формируем расширенный комментарий с информацией о клиенте
      let comment = `Списание приза ${prizeData?.prize?.id} через приложение`;
      
      if (owner) {
        const clientInfo = [];
        
        // ФИО клиента
        if (owner.firstName || owner.lastName) {
          const fullName = [owner.firstName, owner.lastName].filter(Boolean).join(' ');
          clientInfo.push(`ФИО клиента: ${fullName}`);
        }
        
        // Телефон клиента
        if (owner.phone) {
          clientInfo.push(`Телефон клиента: ${owner.phone}`);
        }
        
        // Telegram ID
        if (owner.telegramId || owner.telegram_id) {
          clientInfo.push(`Telegram ID: ${owner.telegramId || owner.telegram_id}`);
        }
        
        // Бонусы клиента
        if (owner.balance !== undefined && owner.balance !== null) {
          clientInfo.push(`Всего бонусов: ${owner.balance}`);
        }
        
        // Сумма покупок
        if (owner.totalSpent !== undefined && owner.totalSpent !== null) {
          clientInfo.push(`Всего потратил: ${parseFloat(owner.totalSpent).toFixed(2)} ₽`);
        }
        
        // Количество покупок
        if (owner.totalPurchases !== undefined && owner.totalPurchases !== null) {
          clientInfo.push(`Количество покупок: ${owner.totalPurchases}`);
        }
        
        // Средний чек
        if (owner.avgCheck !== undefined && owner.avgCheck !== null && owner.avgCheck > 0) {
          clientInfo.push(`Средний чек: ${parseFloat(owner.avgCheck).toFixed(2)} ₽`);
        }
        
        // Статус клиента
        if (owner.status) {
          clientInfo.push(`Статус: ${owner.status}`);
        }
        
        // Премиум статус
        if (owner.isPremium) {
          clientInfo.push(`Telegram Premium: Да`);
        }
        
        // Последняя покупка
        if (owner.lastPurchaseDate) {
          const lastPurchase = new Date(owner.lastPurchaseDate);
          const formattedDate = lastPurchase.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
          clientInfo.push(`Последняя покупка: ${formattedDate}`);
        }
        
        if (clientInfo.length > 0) {
          comment += '\n' + clientInfo.join('\n');
        }
      }

      // ВАЖНО: Backend сам списывает товар в 1С и обновляет статус приза в БД
      // Теперь НЕ нужно вызывать consumeProduct() отдельно!
      console.log('🔄 Отправляем запрос на списание приза в backend...');
      console.log('   Backend сам спишет товар в 1С и обновит статус в БД');

      // ВАЖНО: Используем shop.code из выбранного товара, а НЕ из shopInfo!
      // Товары могут быть из разных магазинов, поэтому берем код магазина товара
      const actualShopCode = selectedProduct?.shop?.code || shopInfo.shop_code;
      console.log(`   Shop Code для списания: ${actualShopCode} (из товара: ${selectedProduct?.shop?.name})`);

      // Списываем приз через backend (он сам сделает списание в 1С + обновление БД)
      const prizeResult = await claimPrize(
        prizeData.prize.id,
        selectedProduct?.id,
        selectedModificationId,
        shopInfo.shop_name,
        telegramData.chat?.id || telegramData.user?.id,
        false, // giftPrize
        actualShopCode, // shopCode ИЗ ТОВАРА для списания в 1С
        comment // Детальный комментарий с информацией о клиенте
      );

      if (prizeResult.success) {
        showTelegramAlert('✅ Приз списан, можете выдавать!');
        // Обновляем статус приза в данных
        if (prizeData && prizeData.prize) {
          prizeData.prize.isSold = true;
        }
        navigate('/');
      } else {
        showTelegramAlert(`❌ Ошибка списания приза: ${prizeResult.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('❌ Ошибка в процессе списания:', error);
      let errorDetails = error.message || String(error);
      if (error.response?.data) {
        errorDetails += ' | ' + JSON.stringify(error.response.data);
      } else if (error.status) {
        errorDetails += ` | Status: ${error.status}`;
      }
      showTelegramAlert(`❌ Ошибка при списании. Попробуйте позже.\n\nДетали: ${errorDetails}`);
    } finally {
      isProcessingRef.current = false;
      setIsClaiming(false);
    }
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!giftPrize.trim()) return;

    await handleConfirmClaim(giftPrize.trim());
  };

  const handleConfirmClaim = async (giftPrizeValue) => {
    // Защита от двойного клика
    if (isProcessingRef.current || isClaiming) {
      return;
    }

    if (!prizeData?.prize?.id || !shopInfo?.shop_name || !telegramData) {
      showTelegramAlert('❌ Ошибка: недостаточно данных для списания приза');
      return;
    }

    isProcessingRef.current = true;

    try {
      setIsClaiming(true);
      setIsClaimModalOpen(false);

      const chatId = telegramData.chat?.id || telegramData.user?.id;
      let selectedProductId, selectedModificationIdForApi, productInfo;

      if (shopInfo?.shop_type === 'mda') {
        // Для MDA магазинов передаем ID товара и характеристики
        selectedProductId = selectedMdaProduct?.id || null;
        selectedModificationIdForApi = selectedMdaCharacteristics;
        productInfo = selectedMdaProduct
          ? `товар: ${selectedMdaProduct.name}`
          : 'товар не выбран';
      } else {
        // Для обычных магазинов передаем ID товара и ID модификации
        selectedProductId = selectedProduct?.id || null;
        selectedModificationIdForApi = selectedModificationId;
        productInfo = selectedProduct
          ? `товар: ${selectedProduct.name}`
          : 'товар не выбран';
      }

      const result = await claimPrize(
        prizeData.prize.id,
        selectedProductId,
        selectedModificationIdForApi,
        shopInfo.shop_name,
        chatId,
        giftPrize,
        shopInfo.shop_code // shopCode для списания в 1С
      );

      if (result.success) {
        showTelegramAlert(`✅ Приз успешно списан (${productInfo}), можете выдавать`);
        // Обновляем статус приза в данных
        if (prizeData && prizeData.prize) {
          prizeData.prize.isSold = true;
        }
        navigate('/');
      } else {
        showTelegramAlert(`❌ Ошибка при списании: ${result.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Error claiming prize:', error);

      // Определяем тип ошибки и показываем соответствующее сообщение
      let errorMessage = '❌ Ошибка при списании приза';

      if (error.message.includes('400')) {
        errorMessage = '❌ Приз уже списан или недоступен';
      } else if (error.message.includes('404')) {
        errorMessage = '❌ Приз не найден';
      } else if (error.message.includes('500')) {
        errorMessage = '❌ Ошибка сервера, попробуйте позже';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = '❌ Ошибка соединения, проверьте интернет';
      } else {
        errorMessage = `❌ Ошибка при списании: ${error.message}`;
      }

      let errorDetails = error.message || String(error);
      if (error.response?.data) {
        errorDetails += ' | ' + JSON.stringify(error.response.data);
      } else if (error.status) {
        errorDetails += ` | Status: ${error.status}`;
      }
      errorMessage += `\n\nДетали: ${errorDetails}`;

      showTelegramAlert(errorMessage);
    } finally {
      isProcessingRef.current = false;
      setIsClaiming(false);
    }
  };

  if (!prizeData || !shopInfo || !telegramData) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} flex items-center justify-center animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="text-center animate-scale-in">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4 text-tg-text"></div>
          <p className="text-tg-hint text-sm">Загрузка приза...</p>
        </div>
      </div>
    );
  }

  const { prize, owner } = prizeData;

  // Проверяем, что данные приза существуют и не пустые
  if (!prize || Object.keys(prize).length === 0 || !prize.name) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="max-w-sm mx-auto py-8">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackClick}
              className="p-2 rounded-xl hover:bg-tg-secondary-bg transition-colors"
            >
              <svg className="w-5 h-5 text-tg-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-tg-text ml-3">Приз не найден</h1>
          </div>

          <div className="tg-card text-center animate-scale-in">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-tg-text mb-2">Приз не найден</h2>
            <p className="text-tg-hint text-sm mb-4">
              QR код не найден в базе данных
            </p>
            <button
              onClick={handleBackClick}
              className="tg-button w-full"
            >
              Вернуться назад
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'text-tg-hint bg-tg-secondary-bg';
      case 'uncommon': return 'text-tg-text bg-tg-secondary-bg';
      case 'rare': return 'text-tg-text bg-tg-secondary-bg';
      case 'epic': return 'text-tg-text bg-tg-secondary-bg';
      case 'legendary': return 'text-tg-text bg-tg-secondary-bg';
      default: return 'text-tg-hint bg-tg-secondary-bg';
    }
  };

  const getRarityLabel = (rarity) => {
    switch (rarity) {
      case 'common': return 'Обычный';
      case 'uncommon': return 'Необычный';
      case 'rare': return 'Редкий';
      case 'epic': return 'Эпический';
      case 'legendary': return 'Легендарный';
      default: return 'Обычный';
    }
  };

  return (
    <div className={`min-h-screen tg-gradient-${theme} animate-fade-in safe-area-top safe-area-bottom`}>
      <div className="max-w-sm mx-auto py-4 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBackClick}
            className="p-2 rounded-xl hover:bg-tg-secondary-bg transition-colors"
          >
            <svg className="w-5 h-5 text-tg-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-tg-text">
            {isClaimModalOpen ? '🎁 Списание приза' : showProductSelection ? 'Выбор товара' : showNoProductsMessage ? 'Приз' : 'Приз'}
          </h1>
          <div className="w-9"></div> {/* Spacer for centering */}
        </div>

        {isClaimModalOpen ? (
          // Ввод товара для списания приза
          <div className="animate-slide-up">
            <div className="mb-6">
              <p className="text-tg-hint text-sm mb-6 leading-relaxed">
                Укажите товар, который вы отдаете клиенту в обмен на приз:
              </p>

              <form onSubmit={handleClaimSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-tg-text mb-3">
                    Товар для выдачи
                  </label>
                  <textarea
                    value={giftPrize}
                    onChange={(e) => setGiftPrize(e.target.value)}
                    placeholder="Например: EPE 15000, Вкус: Малина"
                    className="tg-input resize-none"
                    rows="4"
                    autoFocus
                    disabled={isClaiming}
                  />
                </div>

                <div className="bg-tg-secondary-bg rounded-xl p-4 mb-6">
                  <div className="text-xs text-tg-hint mb-2 font-semibold">💡 Примеры:</div>
                  <div className="text-xs text-tg-text space-y-1">
                    <div>• EPE 15000, Вкус: "Малина"</div>
                    <div>• IQOS 3 DUO, Цвет: "Синий"</div>
                    <div>• JUUL, Вкус: "Мята"</div>
                  </div>
                </div>
              </form>
            </div>

            {/* Prize Info */}
            <div className="tg-card mb-6">
              <div className="flex items-center space-x-3 mb-3">
                {prizeData.prize?.imageUrl && prizeData.prize.imageUrl.startsWith('http') ? (
                  <img
                    src={prizeData.prize.imageUrl}
                    alt={prizeData.prize.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-tg-secondary-bg flex items-center justify-center">
                    <svg className="w-6 h-6 text-tg-hint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-tg-text">
                    {prizeData.prize?.name}
                  </h3>
                  <p className="text-xs text-tg-hint">
                    {prizeData.owner?.firstName} {prizeData.owner?.lastName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : showNoProductsMessage ? (
          // Сообщение о том, что нет доступных товаров
          <div className="animate-slide-up">
            <div className="tg-card text-center">
              <div className="text-orange-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-tg-text mb-2">
                Нет подходящих товаров
              </h2>
              <p className="text-tg-hint text-sm mb-4">
                У вас нет нужного товара для этого приза на филиале.
              </p>

              {/* Список магазинов с товарами для приза */}
              {loadingShops ? (
                <div className="mt-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tg-button mx-auto mb-2"></div>
                  <p className="text-xs text-tg-hint">Ищем другие точки...</p>
                </div>
              ) : availableShops.length > 0 ? (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-tg-text">
                      Доступные точки ({availableShops.length})
                    </h3>
                  </div>

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableShops.map((shop) => (
                      <div
                        key={shop.code}
                        className="flex items-center justify-between p-3 bg-tg-bg rounded-lg hover:bg-tg-secondary-bg transition-colors duration-150"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="flex-shrink-0 w-8 h-8 bg-tg-button rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-tg-button-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-tg-text">
                              {[shop.city, shop.address].filter(Boolean).join(', ')}
                            </p>
                            <p className="text-xs text-tg-hint">
                              {shop.productsCount} товар{shop.productsCount !== 1 ? 'ов' : ''}
                            </p>
                          </div>
                        </div>

                        <div className="flex-shrink-0 ml-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-6">
                  <p className="text-xs text-tg-hint">
                    Нет других точек с подходящими товарами для этого приза
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : !showProductSelection ? (
          <div className="space-y-4 animate-slide-up">
            {/* Prize Image */}
            <div className="relative overflow-hidden rounded-2xl shadow-soft">
              {prize.imageUrl && prize.imageUrl.startsWith('http') ? (
                <div className="w-full aspect-square rounded-2xl overflow-hidden">
                  <img
                    src={prize.imageUrl}
                    alt={prize.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log('Image load error:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={(e) => {
                      console.log('Image loaded successfully:', e.target.src);
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              ) : null}
              <div className={`w-full aspect-square items-center justify-center rounded-2xl ${prize.imageUrl && prize.imageUrl.startsWith('http') ? 'hidden' : 'flex'} bg-tg-secondary-bg`}>
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-tg-hint mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs text-tg-hint">Изображение недоступно</p>
                </div>
              </div>
            </div>

            {/* Rarity Badge */}
            <div className="flex justify-center">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRarityColor(prize.rarity)}`}>
                {getRarityLabel(prize.rarity)}
              </span>
            </div>

            {/* Prize Info */}
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-tg-text mb-2">
                  {prize.name}
                </h2>
                {prize.description && (
                  <p className="text-tg-hint text-sm leading-relaxed">
                    {prize.description.replace(/\\n/g, '\n')}
                  </p>
                )}
              </div>

              {/* Additional Details */}
              {prize.detalPrizeName && (
                <div className="bg-tg-secondary-bg rounded-xl p-4">
                  <div className="text-sm text-tg-hint mb-2 font-medium">Что делать?</div>
                  <div className="text-sm text-tg-text leading-relaxed">
                    {prize.detalPrizeName.replace(/\\n/g, '\n')}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-tg-secondary-bg rounded-xl">
                <span className="text-sm text-tg-hint">Статус</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${prize.isSold ? 'bg-tg-secondary-bg text-tg-hint border border-tg-hint' : 'bg-tg-secondary-bg text-tg-text border border-tg-hint'}`}>
                  {prize.isSold ? 'Продано' : 'Доступен'}
                </span>
              </div>
            </div>

            {/* Owner Info */}
            {owner && (
              <div className="bg-tg-secondary-bg rounded-xl p-4">
                <h3 className="text-sm font-medium text-tg-text mb-3">
                  Владелец
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-tg-hint">Имя</span>
                    <span className="text-sm font-medium text-tg-text">
                      {owner.firstName} {owner.lastName}
                    </span>
                  </div>
                  {owner.phone && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-tg-hint">Телефон</span>
                      <span className="text-sm font-medium text-tg-text">
                        {owner.phone}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : !selectedProduct ? (
          // Выбор товара
          <div className="animate-slide-up">
            <div className="mb-4">
              <p className="text-sm text-tg-hint">
                Выберите товар для списания приза
              </p>
            </div>
            {prizeData.availableProducts && prizeData.availableProducts.length > 0 ? (
              <div className="space-y-3">
                {prizeData.availableProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className="w-full p-4 bg-tg-secondary-bg hover:bg-tg-secondary-bg rounded-xl text-left transition-colors"
                  >
                    <div className="font-medium text-tg-text mb-1">
                      {product.name}
                    </div>
                    {product.categoryName && (
                      <div className="text-sm text-tg-hint">
                        {product.categoryName}
                      </div>
                    )}
                  
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-tg-hint">
                  Нет доступных товаров
                </p>
              </div>
            )}
          </div>
        ) : !selectedProduct ? (
          // Выбор товара
          <div className="animate-slide-up space-y-4">
            <div className="p-4 bg-tg-secondary-bg rounded-xl">
              <div className="font-medium text-tg-text mb-1">
                Выбран товар: {selectedProduct.name}
              </div>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setSelectedModificationId(null);
                  setProductModifications([]);
                }}
                className="text-xs text-tg-link underline"
              >
                Выбрать другой товар
              </button>
            </div>

            {loadingModifications ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tg-button mx-auto"></div>
                <p className="text-sm text-tg-hint mt-3">
                  Загрузка модификаций...
                </p>
              </div>
            ) : productModifications.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-tg-hint">
                  Выберите модификацию товара:
                </p>
                <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                  {productModifications.map((modification) => (
                    <button
                      key={modification.id}
                      onClick={() => handleModificationSelect(modification.id)}
                      className={`w-full p-4 rounded-xl text-left border transition-colors ${
                        selectedModificationId === modification.id
                          ? 'bg-tg-button text-tg-button-text border-tg-button'
                          : 'bg-tg-secondary-bg hover:bg-tg-secondary-bg border-tg-hint text-tg-text'
                      }`}
                    >
                      <div className="font-medium text-sm">
                        {modification.name}
                      </div>
                      {modification.quanty && (
                        <div className="text-xs text-tg-hint mt-1">
                          В наличии: {modification.quanty}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-tg-hint">
                  Модификации не найдены
                </p>
              </div>
            )}
          </div>
        ) : (
          // Выбор модификаций товара
          <div className="animate-slide-up space-y-4">
            <div className="p-4 bg-tg-secondary-bg rounded-xl">
              <div className="font-medium text-tg-text mb-1">
                Выбран товар: {selectedProduct.name}
              </div>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setSelectedModificationId(null);
                  setProductModifications([]);
                }}
                className="text-xs text-tg-link underline"
              >
                Выбрать другой товар
              </button>
            </div>

            {loadingModifications ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tg-button mx-auto"></div>
                <p className="text-sm text-tg-hint mt-3">
                  Загрузка модификаций...
                </p>
              </div>
            ) : productModifications.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-tg-hint">
                  Выберите модификацию товара:
                </p>
                <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                  {productModifications.map((modification) => (
                    <button
                      key={modification.id}
                      onClick={() => handleModificationSelect(modification.id)}
                      className={`w-full p-4 rounded-xl text-left border transition-colors ${
                        selectedModificationId === modification.id
                          ? 'bg-tg-button text-tg-button-text border-tg-button'
                          : 'bg-tg-secondary-bg hover:bg-tg-secondary-bg border-tg-hint text-tg-text'
                      }`}
                    >
                      <div className="font-medium text-sm">
                        {modification.name}
                      </div>
                      {modification.quanty && (
                        <div className="text-xs text-tg-hint mt-1">
                          В наличии: {modification.quanty}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-tg-hint">
                  Модификации не найдены
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom bg-tg-bg/80 backdrop-blur-sm border-t border-tg-hint">
          <div className="max-w-sm mx-auto">
            {isClaimModalOpen ? (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsClaimModalOpen(false);
                    setGiftPrize('');
                  }}
                  disabled={isClaiming}
                  className="flex-1 tg-button-secondary"
                >
                  Отмена
                </button>
                <button
                  onClick={handleClaimSubmit}
                  disabled={!giftPrize.trim() || isClaiming}
                  className="flex-1 tg-button"
                >
                  {isClaiming ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>Списываю...</span>
                    </div>
                  ) : (
                    'Списать приз'
                  )}
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                {!prize.isSold && !showNoProductsMessage && (
                  <button
                    onClick={selectedModificationId ? handleConfirmProductSelection : selectedProduct ? () => {
                      setSelectedProduct(null);
                      setSelectedModificationId(null);
                      setProductModifications([]);
                    } : handleClaimPrize}
                    disabled={isClaiming || (selectedProduct && !selectedModificationId)}
                    className={`w-full tg-button ${isClaiming ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {selectedModificationId ? 'Выбрать модификацию' : selectedProduct ? 'Назад к товарам' : showProductSelection ? 'Назад к призу' : 'Списать приз'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add padding to account for fixed footer */}
        <div className="pb-20"></div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative w-full max-w-sm bg-tg-secondary-bg rounded-2xl shadow-2xl transform transition-all duration-300 ease-out">
            {/* Header */}
            <div className="bg-tg-bg rounded-t-2xl border-b border-tg-hint">
              <div className="flex items-center justify-between p-4">
                <h2 className="text-lg font-bold text-tg-text">
                  Подтверждение
                </h2>
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="p-1.5 rounded-full hover:bg-tg-secondary-bg transition-colors"
                >
                  <svg className="w-5 h-5 text-tg-hint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tg-secondary-bg border border-tg-hint rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-tg-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-tg-text mb-2">
                  Вы точно хотите списать этот товар?
                </h3>
                <div className="text-sm text-tg-hint space-y-1">
                  <p><strong>Товар:</strong> {selectedProduct?.name}</p>
                  {productModifications.find(m => m.id === selectedModificationId) && (
                    <p><strong>Модификация:</strong> {productModifications.find(m => m.id === selectedModificationId).name}</p>
                  )}
                  <p><strong>Приз:</strong> {prizeData?.prize?.name}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-tg-bg rounded-b-2xl border-t border-tg-hint p-4">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="flex-1 tg-button-secondary"
                >
                  Отмена
                </button>
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    handleConfirmProductSelection();
                  }}
                  className="flex-1 tg-button"
                >
                  Списать товар
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrizePage;
